import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  FormInput, 
  Plus, 
  Edit, 
  Trash2, 
  GripVertical,
  Search,
  Settings2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'url', label: 'URL' },
];

const ENTITY_TYPES = [
  { value: 'client', label: 'Clients' },
  { value: 'case', label: 'Cases' },
  { value: 'lead', label: 'Leads' },
];

export const CustomFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeEntity, setActiveEntity] = useState('client');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    field_type: 'text',
    entity_type: 'client',
    options: [],
    required: false,
    default_value: '',
    placeholder: '',
  });

  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    fetchFields();
  }, [activeEntity]);

  const fetchFields = async () => {
    try {
      const response = await api.get('/custom-fields', {
        params: { entity_type: activeEntity }
      });
      setFields(response.data);
    } catch (error) {
      console.error('Failed to fetch custom fields:', error);
      if (error.response?.status === 403) {
        toast.error('Partner role or higher required');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingField) {
        await api.put(`/custom-fields/${editingField.field_id}`, formData);
        toast.success('Field updated');
      } else {
        await api.post('/custom-fields', formData);
        toast.success('Field created');
      }
      fetchFields();
      resetForm();
    } catch (error) {
      toast.error('Failed to save field');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/custom-fields/${deleteId}`);
      toast.success('Field deleted');
      fetchFields();
    } catch (error) {
      toast.error('Failed to delete field');
    } finally {
      setDeleteId(null);
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      field_type: field.field_type,
      entity_type: field.entity_type,
      options: field.options || [],
      required: field.required,
      default_value: field.default_value || '',
      placeholder: field.placeholder || '',
    });
    setIsDialogOpen(true);
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    setFormData({
      ...formData,
      options: [...formData.options, newOption.trim()],
    });
    setNewOption('');
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index),
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      field_type: 'text',
      entity_type: activeEntity,
      options: [],
      required: false,
      default_value: '',
      placeholder: '',
    });
    setEditingField(null);
    setIsDialogOpen(false);
  };

  const getFieldTypeLabel = (type) => {
    return FIELD_TYPES.find(t => t.value === type)?.label || type;
  };

  if (loading) {
    return <PageLoader text="Loading custom fields..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="custom-fields-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Custom Fields</h1>
          <p className="text-slate-500 mt-1">Add custom data fields to clients, cases, and leads</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-field-btn">
              <Plus className="h-4 w-4" />
              Add Field
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingField ? 'Edit Field' : 'Create Custom Field'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Field Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Referral Source"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="field_type">Field Type *</Label>
                  <Select value={formData.field_type} onValueChange={(v) => setFormData({ ...formData, field_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="entity_type">Apply To *</Label>
                  <Select value={formData.entity_type} onValueChange={(v) => setFormData({ ...formData, entity_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ENTITY_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(formData.field_type === 'select' || formData.field_type === 'multiselect') && (
                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Add option"
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={addOption}>Add</Button>
                  </div>
                  {formData.options.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.options.map((opt, i) => (
                        <Badge key={i} variant="secondary" className="gap-1">
                          {opt}
                          <button type="button" onClick={() => removeOption(i)} className="ml-1 hover:text-red-600">×</button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="placeholder">Placeholder</Label>
                  <Input
                    id="placeholder"
                    value={formData.placeholder}
                    onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                    placeholder="Enter hint text"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="default_value">Default Value</Label>
                  <Input
                    id="default_value"
                    value={formData.default_value}
                    onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="required"
                  checked={formData.required}
                  onCheckedChange={(c) => setFormData({ ...formData, required: c })}
                />
                <Label htmlFor="required">Required field</Label>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit">{editingField ? 'Update' : 'Create'} Field</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Entity Tabs */}
      <Tabs value={activeEntity} onValueChange={setActiveEntity}>
        <TabsList>
          <TabsTrigger value="client">Clients</TabsTrigger>
          <TabsTrigger value="case">Cases</TabsTrigger>
          <TabsTrigger value="lead">Leads</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Fields List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            Custom Fields for {ENTITY_TYPES.find(e => e.value === activeEntity)?.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {fields.length > 0 ? (
            <div className="space-y-2">
              {fields.map((field, index) => (
                <div 
                  key={field.field_id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-slate-400 cursor-grab" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{field.name}</span>
                        {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Badge variant="outline">{getFieldTypeLabel(field.field_type)}</Badge>
                        {field.options?.length > 0 && (
                          <span>{field.options.length} options</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(field)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(field.field_id)} className="text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FormInput className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>No custom fields for {ENTITY_TYPES.find(e => e.value === activeEntity)?.label.toLowerCase()}</p>
              <p className="text-sm">Click "Add Field" to create one</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Field?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the field definition. Existing data in this field will not be deleted but will no longer be displayed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomFields;
